import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { finalize, Subscription } from 'rxjs';
import { AttendanceService } from '../../../attendance/attendance.service';
import { AttendanceLog } from '../../../attendance/attendance.model';
import { EmployeesService } from '../../../employees/employees.service';
import { Employee } from '../../../employees/employees.model';
import { PaginatedResponse } from '../../../common/models/common.model';
import { buildFilterQuery } from '../../../utils';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SnackbarService } from '../../../services/snackbar.service';
import { SnackbarAlertStatus } from '../../../utils';

@Component({
  selector: 'app-employee-travel-log',
  standalone: false,
  templateUrl: './employee-travel-log.component.html',
})
export class EmployeeTravelLogComponent implements OnInit, OnDestroy {
  @ViewChild('logDetailModal') logDetailModal!: TemplateRef<any>;
  @ViewChild('downloadReportModal') downloadReportModal!: TemplateRef<any>;

  protected isLoading: boolean = false;
  protected isDownloading: boolean = false;
  protected employeeId!: string;
  protected employee: Employee | null = null;
  protected logs: AttendanceLog[] = [];
  protected selectedLog: AttendanceLog | null = null;
  protected currentPage: number = 1;
  protected totalCount: number = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;

  protected downloadForm = { start_date: '', end_date: '' };
  protected logReportMode: 'month' | 'year' | 'range' | 'month_range' = 'month';
  protected logReportFilters = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    start_month: 1,
    end_month: new Date().getMonth() + 1,
    start_date: '',
    end_date: '',
  };

  protected logFilterMode: 'all' | 'date' | 'range' | 'month' | 'year' = 'all';
  protected logFilters = {
    date: '',
    start_date: '',
    end_date: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  };

  protected readonly logFilterModes: { label: string; value: 'all' | 'date' | 'range' | 'month' | 'year' }[] = [
    { label: 'All records', value: 'all' },
    { label: 'Specific Date', value: 'date' },
    { label: 'Date Range', value: 'range' },
    { label: 'Month + Year', value: 'month' },
    { label: 'Year', value: 'year' },
  ];

  protected readonly monthOptions = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  private _subscription: Subscription = new Subscription();
  private _modalRef?: NgbModalRef;

  constructor(
    private _route: ActivatedRoute,
    private _attendanceService: AttendanceService,
    private _employeesService: EmployeesService,
    private _modalService: NgbModal,
    private _snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.employeeId = this._route.snapshot.params['id'];
    this._getEmployee();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this._getLogs();
  }

  protected onLogFilterModeChange(): void {
    this.onApplyLogFilters();
  }

  protected onApplyLogFilters(): void {
    if (this.logFilterError) return;

    this.currentPage = 1;
    this._getLogs();
  }

  protected onClearLogFilters(): void {
    this.logFilterMode = 'all';
    this.logFilters = {
      date: '',
      start_date: '',
      end_date: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
    };
    this.currentPage = 1;
    this._getLogs();
  }

  protected get logFilterError(): string | null {
    const { date, start_date, end_date, month, year } = this.logFilters;

    if (this.logFilterMode === 'date' && !date) return 'Select a date.';
    if (this.logFilterMode === 'range') {
      if (!start_date || !end_date) return 'Select both a start and end date.';
      if (start_date > end_date) return 'Start date must be on or before end date.';
    }
    if (this.logFilterMode === 'month' && !month) return 'Select a month.';
    if ((this.logFilterMode === 'month' || this.logFilterMode === 'year') && !year) {
      return this.logFilterMode === 'month' ? "'Year' is required when filtering by month." : 'Select a year.';
    }

    return null;
  }

  protected viewLogDetail(log: AttendanceLog): void {
    this.selectedLog = log;
    this._modalRef = this._modalService.open(this.logDetailModal, { size: 'lg', scrollable: true });
  }

  protected openDownloadModal(): void {
    this.logReportFilters = {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      start_month: 1,
      end_month: new Date().getMonth() + 1,
      start_date: '',
      end_date: '',
    };

    // Seed the report with the period currently shown in the table.
    switch (this.logFilterMode) {
      case 'date':
        this.logReportMode = 'range';
        this.logReportFilters.start_date = this.logFilters.date;
        this.logReportFilters.end_date = this.logFilters.date;
        break;
      case 'range':
        this.logReportMode = 'range';
        this.logReportFilters.start_date = this.logFilters.start_date;
        this.logReportFilters.end_date = this.logFilters.end_date;
        break;
      case 'month':
        this.logReportMode = 'month';
        this.logReportFilters.year = this.logFilters.year;
        this.logReportFilters.month = this.logFilters.month;
        break;
      case 'year':
        this.logReportMode = 'year';
        this.logReportFilters.year = this.logFilters.year;
        break;
      default:
        this.logReportMode = 'month';
    }

    this._modalRef = this._modalService.open(this.downloadReportModal, { size: 'md', backdrop: 'static' });
  }

  protected onDownloadReport(): void {
    this.isDownloading = true;

    const { year, month, start_month, end_month, start_date, end_date } = this.logReportFilters;
    const employeeId = String(this.employee?.user?.id ?? '');

    const payload: Record<string, any> = { include_expenses: true };
    if (employeeId) payload['employee_id'] = employeeId;

    if (this.logReportMode === 'month') {
      payload['year'] = year;
      payload['month'] = month;
    } else if (this.logReportMode === 'year') {
      payload['year'] = year;
    } else if (this.logReportMode === 'month_range') {
      payload['year'] = year;
      payload['start_month'] = start_month;
      payload['end_month'] = end_month;
    } else {
      if (start_date) payload['start_date'] = start_date;
      if (end_date) payload['end_date'] = end_date;
    }

    const filename =
      this.logReportMode === 'month'
        ? `attendance_log_${year}_${String(month).padStart(2, '0')}.xlsx`
        : this.logReportMode === 'year'
          ? `attendance_log_${year}.xlsx`
          : this.logReportMode === 'month_range'
            ? `attendance_log_${year}_m${start_month}-m${end_month}.xlsx`
            : `attendance_log_${start_date}_to_${end_date}.xlsx`;

    const sub = this._attendanceService
      .exportAttendanceLogReport(payload)
      .pipe(finalize(() => (this.isDownloading = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
          this._modalRef?.close();
          this._snackbarService.show('Report downloaded successfully', SnackbarAlertStatus.SUCCESS);
        },
        error: (err: any) => {
          this._snackbarService.show(err.error?.message || 'Failed to generate report', SnackbarAlertStatus.ERROR);
        },
      });
    this._subscription.add(sub);
  }

  protected isDownloadDisabled(): boolean {
    if (this.isDownloading) return true;
    if (this.logReportMode === 'range') return !this.logReportFilters.start_date || !this.logReportFilters.end_date;
    return false;
  }

  private _getEmployee(): void {
    const sub = this._employeesService.get(this.employeeId).subscribe({
      next: (emp: Employee) => {
        this.employee = emp;
        this._getLogs();
      },
    });
    this._subscription.add(sub);
  }

  private _getLogs(): void {
    this.isLoading = true;
    const { date, start_date, end_date, month, year } = this.logFilters;

    const dateParams: Record<string, any> = {};
    if (this.logFilterMode === 'date') {
      dateParams['date'] = date;
    } else if (this.logFilterMode === 'range') {
      // Both bounds are inclusive on the API side (gte / lte).
      dateParams['start_date'] = start_date;
      dateParams['end_date'] = end_date;
    } else if (this.logFilterMode === 'month') {
      dateParams['month'] = month;
      dateParams['year'] = year;
    } else if (this.logFilterMode === 'year') {
      dateParams['year'] = year;
    }

    const params = buildFilterQuery({ page: this.currentPage, ...dateParams });

    const sub = this._attendanceService
      .getAttendanceLogs(this.employee?.user?.id!, params)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: PaginatedResponse<AttendanceLog>) => {
          this.logs = res.results || [];
          this.totalCount = res.count;
          this.hasNextPage = !!res.next;
          this.hasPreviousPage = !!res.previous;
        },
      });
    this._subscription.add(sub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this._modalRef?.dismiss();
  }
}
